import { useState } from 'react'
import api, { apiUpload } from '../../../../../helpers/apiRequester'
import { buildPayload, friendlySubmitError, validateForm } from './militaryHearingLossFormUtils'

// Re-exported for backward compatibility — the value now lives in
// ./militaryHearingLossFormUtils alongside the other serialisation helpers.
export { MILITARY_HEARING_LOSS_INTRODUCER } from './militaryHearingLossFormUtils'

export const TITLES = ['Mr', 'Mrs', 'Miss', 'Ms', 'Mx', 'Dr', 'Prof', 'Sir', 'Dame', 'Rev'] as const

export const SERVICE_BRANCHES = [
    'Army',
    'Royal Navy',
    'RAF',
    'Army Reserve',
    'Royal Navy Reserve',
    'RAF Auxiliary',
] as const

export type ServiceBranch = (typeof SERVICE_BRANCHES)[number] | ''

// 'tbc' = audiogram supplied but the NIHL result has not been calculated yet.
export type AudiologyResult = '>=4dB' | '<4dB' | 'tbc' | ''

export type AlternativeNoiseExposure = 'none' | 'some' | ''

export interface ServiceRecord {
    // Stable client-only id used as the React key and to key per-service errors.
    // Stripped from the payload before submission (see buildPayload).
    id: string
    serviceNumber: string
    branch: ServiceBranch
    enlistmentDate: string
    dischargeDate: string
    // true when the claimant is still serving in this role. When true the
    // discharge date is not required. Used to derive the top-level
    // `activeService` flag; stripped from each record before submission.
    activeService: boolean
}

export interface MilitaryHearingLossFormState {
    // Personal details
    title: string
    firstname: string
    middleNames: string
    lastname: string
    dob: string
    nationalInsurance: string
    address1: string
    address2: string
    address3: string
    address4: string
    postcode: string
    // ISO 3166-1 alpha-2 code of the address country, e.g. GB, DE
    country: string
    homeTelephone: string
    telephone: string // mobile number, as typed (without the dial code)
    // ISO country of the mobile's dial code; combined with telephone in buildPayload
    telephoneCountry: string
    email: string

    // Service records (up to 3)
    services: ServiceRecord[]
    alternateCivilianExposure: boolean | null

    // Medical indicators
    audiology: AudiologyResult
    hearingLossOrTinnitus: boolean | null
    hyperacusis: boolean | null
    alternativeNoiseExposure: AlternativeNoiseExposure

    // Other
    successfulAfcsWarPensionClaim: boolean | null
    afcsAwardAmount: number | null
    nfciClaim: boolean | null
    specialForces: boolean | null

    // Confirmation
    // The introducer agent's explicit confirmation that the client has opted in
    // to enrol in the Matrix journey. Gates the Submit button.
    matrixOptInConfirmed: boolean
}

let serviceIdSeq = 0
const emptyService = (): ServiceRecord => ({
    id: `svc-${serviceIdSeq++}`,
    serviceNumber: '',
    branch: '',
    enlistmentDate: '',
    dischargeDate: '',
    activeService: false,
})

export const EMPTY_FORM: MilitaryHearingLossFormState = {
    title: '',
    firstname: '',
    middleNames: '',
    lastname: '',
    dob: '',
    nationalInsurance: '',
    address1: '',
    address2: '',
    address3: '',
    address4: '',
    postcode: '',
    country: 'GB',
    homeTelephone: '',
    telephone: '',
    telephoneCountry: 'GB',
    email: '',
    services: [emptyService()],
    alternateCivilianExposure: null,
    audiology: '',
    hearingLossOrTinnitus: null,
    hyperacusis: null,
    alternativeNoiseExposure: '',
    successfulAfcsWarPensionClaim: null,
    afcsAwardAmount: null,
    nfciClaim: null,
    specialForces: null,
    matrixOptInConfirmed: false,
}

// Builds a pristine form with a freshly-id'd service record. Used for the
// initial state and on reset so service React keys never collide across resets.
const createEmptyForm = (): MilitaryHearingLossFormState => ({ ...EMPTY_FORM, services: [emptyService()] })

export interface SubmittedContact {
    firstname: string
    lastname: string
    email: string
    telephone: string
}

export interface SendResult {
    sendType: 'email' | 'sms'
    success: boolean
    esignUrl?: string
    message?: string
    sentTo: string
}

export type SendResults = {
    email: SendResult | null
    sms: SendResult | null
}

export type AudiogramUploadState =
    | { status: 'idle' }
    | { status: 'uploading' }
    | { status: 'done'; fileName: string; url?: string }
    | { status: 'error'; message: string }

export const useMilitaryHearingLossForm = (agentLabel: string) => {
    const [form, setForm] = useState<MilitaryHearingLossFormState>(createEmptyForm)
    // The audiogram is held outside `form` because it is a File, is uploaded in
    // a second request after the lead row exists, and is never serialised.
    const [audiogramFile, setAudiogramFile] = useState<File | null>(null)
    const [audiogramUpload, setAudiogramUpload] = useState<AudiogramUploadState>({ status: 'idle' })
    const [poidFile, setPoidFile] = useState<File | null>(null)
    const [utilityBillFile, setUtilityBillFile] = useState<File | null>(null)
    const [identityUpload, setIdentityUpload] = useState<AudiogramUploadState>({ status: 'idle' })
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submitError, setSubmitError] = useState<string | null>(null)
    const [submitted, setSubmitted] = useState(false)
    const [caseRef, setCaseRef] = useState<string | null>(null)
    const [clientId, setClientId] = useState<number | null>(null)
    const [submittedContact, setSubmittedContact] = useState<SubmittedContact | null>(null)
    const [isSending, setIsSending] = useState<null | 'email' | 'sms'>(null)
    const [sendResults, setSendResults] = useState<SendResults>({ email: null, sms: null })

    const setField = <K extends keyof MilitaryHearingLossFormState>(
        field: K,
        value: MilitaryHearingLossFormState[K],
    ) => {
        setForm((prev) => ({ ...prev, [field]: value }))
    }

    const setServiceField = <K extends keyof ServiceRecord>(index: number, field: K, value: ServiceRecord[K]) => {
        setForm((prev) => {
            const services = prev.services.map((s, i) => (i === index ? { ...s, [field]: value } : s))
            return { ...prev, services }
        })
    }

    const addService = () => {
        setForm((prev) => {
            if (prev.services.length >= 3) return prev
            return { ...prev, services: [...prev.services, emptyService()] }
        })
    }

    const removeService = (index: number) => {
        setForm((prev) => {
            if (prev.services.length <= 1) return prev
            return { ...prev, services: prev.services.filter((_, i) => i !== index) }
        })
    }

    const validate = (): string | null => {
        const formError = validateForm(form)
        if (formError) return formError
        if (!audiogramFile) return 'An audiogram upload is required'
        if (!poidFile) return 'Proof of ID is required'
        if (!utilityBillFile) return 'A utility bill is required'
        return null
    }

    const isValid = validate() === null

    const reset = () => {
        setForm(createEmptyForm())
        setAudiogramFile(null)
        setAudiogramUpload({ status: 'idle' })
        setPoidFile(null)
        setUtilityBillFile(null)
        setIdentityUpload({ status: 'idle' })
        setSubmitError(null)
        setSubmitted(false)
        setCaseRef(null)
        setClientId(null)
        setSubmittedContact(null)
        setIsSending(null)
        setSendResults({ email: null, sms: null })
    }

    const uploadAudiogramFor = async (targetCaseRef: string, file: File) => {
        setAudiogramUpload({ status: 'uploading' })
        try {
            const formData = new FormData()
            formData.append('case_ref', targetCaseRef)
            formData.append('audiogram', file, file.name)
            const res = await apiUpload('/military-hearing-loss-upload-audiogram', formData)
            if (res.data?.success) {
                setAudiogramUpload({ status: 'done', fileName: file.name, url: res.data.url })
                return { success: true }
            }
            const message = res.data?.message || 'Audiogram upload failed'
            setAudiogramUpload({ status: 'error', message })
            return { success: false, message }
        } catch (error: any) {
            const message = error?.response?.data?.message || error?.message || 'Audiogram upload failed'
            setAudiogramUpload({ status: 'error', message })
            return { success: false, message }
        }
    }

    const uploadIdentityFor = async (targetCaseRef: string, poid: File | null, utilityBill: File | null) => {
        if (!poid && !utilityBill) return { success: false, message: 'Nothing to upload' }
        setIdentityUpload({ status: 'uploading' })
        try {
            const formData = new FormData()
            formData.append('case_ref', targetCaseRef)
            if (poid) formData.append('poid', poid, poid.name)
            if (utilityBill) formData.append('utility_bill', utilityBill, utilityBill.name)
            const res = await apiUpload('/military-hearing-loss-upload-identity', formData)
            if (res.data?.success) {
                const names = [poid?.name, utilityBill?.name].filter(Boolean).join(' · ')
                setIdentityUpload({ status: 'done', fileName: names })
                return { success: true }
            }
            const message = res.data?.message || 'Identity document upload failed'
            setIdentityUpload({ status: 'error', message })
            return { success: false, message }
        } catch (error: any) {
            const message = error?.response?.data?.message || error?.message || 'Identity document upload failed'
            setIdentityUpload({ status: 'error', message })
            return { success: false, message }
        }
    }

    const retryIdentityUpload = async (next?: { poid?: File; utilityBill?: File }) => {
        if (next?.poid) setPoidFile(next.poid)
        if (next?.utilityBill) setUtilityBillFile(next.utilityBill)
        const poid = next?.poid || poidFile
        const utilityBill = next?.utilityBill || utilityBillFile
        if (!caseRef) return { success: false, message: 'Nothing to upload' }
        return uploadIdentityFor(caseRef, poid, utilityBill)
    }

    /** Retry (or replace) the audiogram for the lead that was just submitted. */
    const retryAudiogramUpload = async (file?: File) => {
        const target = file || audiogramFile
        if (!caseRef || !target) return { success: false, message: 'Nothing to upload' }
        if (file) setAudiogramFile(file)
        return uploadAudiogramFor(caseRef, target)
    }

    const submit = async () => {
        const err = validate()
        if (err) {
            setSubmitError(err)
            return { success: false }
        }
        setSubmitError(null)
        setIsSubmitting(true)
        try {
            const payload = buildPayload(form, agentLabel)
            const res = await api({ url: '/military-hearing-loss-submit-lead', data: payload })
            if (res.data?.success && res.data.case_ref) {
                setCaseRef(res.data.case_ref)
                setClientId(res.data.client_id ?? null)
                setSubmittedContact({
                    firstname: form.firstname,
                    lastname: form.lastname,
                    email: form.email,
                    telephone: payload.telephone,
                })
                setSendResults({ email: null, sms: null })
                setSubmitted(true)
                // The lead row now exists; attach the audiogram. A failure here
                // leaves the lead in place and surfaces a retry on the success panel.
                if (audiogramFile) void uploadAudiogramFor(res.data.case_ref, audiogramFile)
                if (poidFile || utilityBillFile) void uploadIdentityFor(res.data.case_ref, poidFile, utilityBillFile)
                return { success: true }
            }
            setSubmitError(friendlySubmitError(res.data?.message || 'Could not submit lead (unknown error)'))
            return { success: false }
        } catch (error: any) {
            const message = error?.response?.data?.message || error?.message || 'Unknown error'
            setSubmitError(friendlySubmitError(message))
            return { success: false }
        } finally {
            setIsSubmitting(false)
        }
    }

    const sendEsignLink = async (sendType: 'email' | 'sms') => {
        if (!caseRef) return { success: false }
        const recipient = sendType === 'email' ? submittedContact?.email : submittedContact?.telephone
        setIsSending(sendType)
        try {
            const res = await api({
                url: '/military-hearing-loss-send-esign-link',
                data: { case_ref: caseRef, sendType },
            })
            if (res.data?.success) {
                setSendResults((prev) => ({ ...prev, [sendType]: { sendType, success: true, esignUrl: res.data.esignUrl, message: res.data.message, sentTo: recipient || '' } }))
                return { success: true }
            }
            const message = res.data?.message || 'Failed to send link'
            setSendResults((prev) => ({ ...prev, [sendType]: { sendType, success: false, message, sentTo: recipient || '' } }))
            return { success: false, message }
        } catch (error: any) {
            const message = error?.response?.data?.message || error?.message || 'Unknown error'
            setSendResults((prev) => ({ ...prev, [sendType]: { sendType, success: false, message, sentTo: recipient || '' } }))
            return { success: false, message }
        } finally {
            setIsSending(null)
        }
    }

    return {
        form,
        setField,
        setServiceField,
        addService,
        removeService,
        audiogramFile,
        setAudiogramFile,
        audiogramUpload,
        retryAudiogramUpload,
        poidFile,
        setPoidFile,
        utilityBillFile,
        setUtilityBillFile,
        identityUpload,
        retryIdentityUpload,
        submit,
        reset,
        validate,
        isValid,
        isSubmitting,
        submitError,
        submitted,
        caseRef,
        clientId,
        submittedContact,
        isSending,
        sendResults,
        sendEsignLink,
    }
}
